<template>
  <li v-if="item"
    class="flex flex-nowrap justify-between items-center gap-3 sm:gap-4 py-2.5 md:py-3 border-b-[1px] border-b-[#E5E5E5]"
    :class="{ 'flex-row-reverse': isRightToLeft }">
    <!-- Values container - shown on left for RTL, right for LTR -->
    <!-- parent's children - sub-attributes -->
    <!-- if the parent has more than one child -->
    <div v-if="item.children && item.children.length > 1" class="relative flex items-center flex-1 min-w-0">
      <!-- Prev button -->
      <button
        v-if="showPrevButton"
        :aria-label="isRightToLeft ? $t('general.next') : $t('general.previous')"
        @click="scroll(-1)"
        class="border-0 !outline-none items-center z-[2] py-1.5 px-0 cursor-pointer"
      >
        <span class="arrow-shadow active:scale-[0.90] transition-all duration-150 bg-white border active:border-primary overflow-hidden rounded-full p-1.5 grid place-content-center">
          <svg
            class="ltr:rotate-0 rtl:rotate-180 h-[17px] w-[17px]"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          >
            <g fill="none">
              <path fill="currentColor" d="M3.636 11.293a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414-1.414L6.757 13H20a1 1 0 1 0 0-2H6.757l3.95-3.95a1 1 0 0 0-1.414-1.414l-5.657 5.657Z" />
            </g>
          </svg>
        </span>
      </button>

      <!-- Scrollable content container -->
      <div class="relative overflow-x-auto no-scrollbar sm:overscroll-x-none scrollbar-hide flex-1">
        <!-- if non-selectable - if the parent attribtue has a display_as_text value of true -->
        <div v-if="displayAsTextOnly"
          class="nonselectable__attributes__list flex flex-nowrap items-center gap-2.5 text-black font-normal min-w-fit justify-start">
          <NonSelectableAttribute v-for="child in item.children" :key="child.id" :child="child" />
        </div>
        <!-- else if they're selectable -->
        <div v-else class="flex flex-nowrap items-center gap-2.5 text-black font-normal min-w-fit justify-start">
          <SelectableAttribute v-for="child in item.children" :key="child.id" :selectedAttributes="selectedAttributes"
            :addToSelectedAttributes="addToSelectedAttributes" :child="child" />
        </div>
      </div>

      <!-- Next button -->
      <button
        v-if="showNextButton"
        :aria-label="isRightToLeft ? $t('general.previous') : $t('general.next')"
        @click="scroll(1)"
        class="border-0 !outline-none items-center z-[2] py-1.5 px-0 cursor-pointer"
      >
        <span class="arrow-shadow active:scale-[0.90] transition-all duration-150 bg-white border active:border-primary overflow-hidden rounded-full p-1.5 grid place-content-center">
          <svg
            class="ltr:rotate-180 rtl:rotate-0 h-[17px] w-[17px]"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
          >
            <g fill="none">
              <path fill="currentColor" d="M3.636 11.293a1 1 0 0 0 0 1.414l5.657 5.657a1 1 0 0 0 1.414-1.414L6.757 13H20a1 1 0 1 0 0-2H6.757l3.95-3.95a1 1 0 0 0-1.414-1.414l-5.657 5.657Z" />
            </g>
          </svg>
        </span>
      </button>
    </div>
    <!-- else if singular, non-selectable (if it has only one child) -->
    <div v-else-if="item.children && item.children.length === 1 && item.children[0]"
      class="text-black flex items-center gap-2.5 font-normal flex-1 min-w-0 justify-start">
      <NonSelectableAttribute :child="item.children[0]" />
    </div>
    
    <!-- if empty -->
    <div v-else class="flex-1 min-w-0 flex items-center justify-start">--</div>
    
    <!-- Label - shown on right for RTL, left for LTR -->
    <div v-if="item[finalizedNameKey]" ref="labelRef" class="text-black font-normal flex-shrink-0" :style="{ minWidth: labelMinWidth }" :class="isRightToLeft ? 'text-right' : 'text-left'">
      {{ item[finalizedNameKey].split('-')[0] }}
    </div>
  </li>
</template>

<script>
import SelectableAttribute from "./SelectableAttribute.vue";
import NonSelectableAttribute from "./NonSelectableAttribute.vue";

export default {
  name: "attribute",
  setup() {
    const usedCookieLanguage = useCookie('localStorageCopy')?.value?.language || 'ar';
    const showPrevButton = ref(false);
    const showNextButton = ref(false);
    const isRightToLeft = computed(() => usedCookieLanguage === 'ar');
    const labelRef = ref(null);
    const sectionWidth = ref(0);
    const labelMinWidth = computed(() => {
      return sectionWidth.value > 0 ? `${sectionWidth.value / 2}px` : '50%';
    });

    return {
      usedCookieLanguage,
      showPrevButton,
      showNextButton,
      isRightToLeft,
      labelRef,
      sectionWidth,
      labelMinWidth
    }
  },
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    addToSelectedAttributes: {
      type: Function,
      required: true
    },
    selectedAttributes: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  computed: {
    finalizedNameKey() {
      return this.nameKey || `name_${this.usedCookieLanguage}`;
    },
    displayAsTextOnly() {
      return !!this.item?.display_as_text
    }
  },
  components: {
    NonSelectableAttribute,
    SelectableAttribute
  },
  methods: {
    scroll(direction) {
      const container = this.$el.querySelector('.overflow-x-auto');
      if (container) {
        const scrollAmount = container.offsetWidth * 0.3;
        container.scrollBy({
          left: direction * (this.isRightToLeft ? -scrollAmount : scrollAmount),
          behavior: 'smooth'
        });
        this.updateArrowVisibility(container);
      }
    },
    updateArrowVisibility(container) {
      if (container) {
        const scrollLeft = Math.abs(container.scrollLeft);
        const maxScroll = container.scrollWidth - container.clientWidth;
        
        if (this.isRightToLeft) {
          this.showNextButton = scrollLeft < maxScroll;
          this.showPrevButton = scrollLeft > 0;
        } else {
          this.showNextButton = scrollLeft > 0;
          this.showPrevButton = scrollLeft < maxScroll;
        }
      }
    }
  },
  mounted() {
    const container = this.$el.querySelector('.overflow-x-auto');
    if (container) {
      this.updateArrowVisibility(container);
      // Add resize observer to handle container size changes
      const resizeObserver = new ResizeObserver(() => {
        this.updateArrowVisibility(container);
      });
      resizeObserver.observe(container);
      
      container.addEventListener('scroll', () => this.updateArrowVisibility(container));
    }

    // Observe the parent section (li element) to update label min-width dynamically
    if (this.$el) {
      const updateSectionWidth = () => {
        this.sectionWidth = this.$el.offsetWidth;
      };
      
      // Initial calculation
      updateSectionWidth();
      
      // Observe section width changes
      const sectionResizeObserver = new ResizeObserver(() => {
        updateSectionWidth();
      });
      sectionResizeObserver.observe(this.$el);
      
      // Store observer for cleanup
      this._sectionResizeObserver = sectionResizeObserver;
    }
  },
  beforeUnmount() {
    // Clean up ResizeObserver
    if (this._sectionResizeObserver) {
      this._sectionResizeObserver.disconnect();
    }
  },
};
</script>
<style lang="scss" scoped >
.nonselectable__attributes__list {
  span.attribute__text__label {
    &::after {
      content: ",";
      color: #7a7a7a;
    }

    &:last-of-type {
      &::after {
        content: ""
      }
    }
  }
}
// if Arabic, add an Arabian Comma
html.rtl{
  .nonselectable__attributes__list {
    span.attribute__text__label {
      &::after {
        content: "،";
      }   
      &:last-of-type {
        &::after {
          content: ""
        }
      }
    }

  }
}

</style>